import { Rate, type RateJson } from "./rate";
import { WithRates } from "./with-rates";

type Section = {
  id: number;
  name: string;
  uuid: string;
  capacity: number | null;
  isNumbered: boolean;
  accessPoint: null;
  availability: number;
};

type SectorJson = {
  id: number;
  name: string;
  uuid: string | null;
  color?: string | null;
  isNumbered: boolean | null;
  rates: RateJson[];
  sections?: Section[];
  additionals?: SectorJson[] | null;
  productType: "SIMPLE" | "COMBO" | "BUNDLE" | undefined;
};

class Sector extends WithRates {
  private readonly id: number;
  private readonly name: string;
  private readonly uuid: string | null;
  private readonly color?: string | null;
  private readonly numbered: boolean;
  private readonly sections: readonly Section[];
  private readonly additionals: readonly Sector[];

  static fromJson(json: SectorJson, noServiceCharge = false): Sector {
    return new Sector(
      json.id,
      json.name,
      json.uuid,
      json.color,
      json.isNumbered ?? false,
      json.rates.map((rate) => Rate.fromJson(rate, false, noServiceCharge)),
      json.sections,
      json.additionals?.map((additional) => Sector.fromJson(additional, noServiceCharge)) ?? [],
    );
  }

  constructor(
    id: number,
    name: string,
    uuid: string | null = null,
    color: string | undefined | null = null,
    isNumbered = false,
    rates: Rate[] = [],
    sections: Section[] = [],
    additionals: Sector[] = [],
  ) {
    super(rates);
    this.id = id;
    this.name = name;
    this.uuid = uuid;
    this.color = color;
    this.numbered = isNumbered;
    this.sections = sections.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
    this.additionals = additionals;
    // (a, b) => a.name > b.name ? 1 : b.name > a.name ? -1 : 0
  }

  getId() {
    return this.id;
  }

  getName() {
    return this.name;
  }

  getColor() {
    return this.color;
  }

  getUuid() {
    return this.uuid;
  }

  isNumbered() {
    return this.numbered;
  }

  getAdditionals() {
    return this.additionals;
  }

  hasAdditionals() {
    return this.additionals.length > 0;
  }

  mapAdditionals<T>(mapper: (additional: Sector) => T) {
    return this.additionals.map(mapper);
  }

  findAdditionalById(id: number) {
    return this.additionals.find((additional) => additional.getId() === id);
  }

  isAvailable() {
    return this.hasAnyRateAvailable() && this.sections.some((section) => section.availability > 0);
  }

  getAvailableSectionsUuids() {
    return this.hasAnyRateAvailable()
      ? this.sections.filter((section) => section.availability > 0).map((section) => section.uuid)
      : [];
  }

  findSectionByUuid(uuid: string) {
    return this.sections.find((section) => section.uuid === uuid);
  }

  findSectionByName(name: string) {
    return this.sections.find((section) => section.name === name);
  }

  mapSections<T>(mapper: (section: Section) => T) {
    return this.sections.map(mapper);
  }
}

export { Sector };
export type { Section, SectorJson };
